'use client';

import { loadLanguagePreference } from '../i18n';
import { clearLocalSwrCache } from '../swr/local-cache-middleware';
import { getResearchDomain } from './rsearch';
import { withRequestId } from './request-id';

// 流式接口路径列表 - 这些接口使用 text/event-stream
// 注意：浏览器环境无法通过 JavaScript 控制 HTTP 协议版本
// HTTP/1.1 vs HTTP/2 由浏览器和服务器自动协商
// 如需强制使用 HTTP/1.1，需在服务器端（Nginx/Next.js）配置
const STREAMING_API_PATHS = [
  '/api/ask',
  '/api/ask/tasks/',
  '/api/ask-pro',
  // '/api/chat',
  // '/api/predic/chat/stream',
  '/api/suggestions',
];

/**
 * 检查给定的 URL 是否为流式接口
 */
function isStreamingAPI(url: string): boolean {
  return STREAMING_API_PATHS.some((path) => url.includes(path));
}

/**
 * 为流式 API 重写 URL（如果配置了 STREAM_API_ROOT_URL）
 * @param url 原始 URL
 * @returns 重写后的 URL 或原始 URL
 */
function rewriteStreamingAPIUrl(url: string): string {
  // 获取构建时注入的环境变量（必须使用 NEXT_PUBLIC_ 前缀才能在客户端访问）
  const streamApiRootUrl = process.env.NEXT_PUBLIC_STREAM_API_ROOT_URL;

  // 如果环境变量不存在或为空，返回原 URL
  if (!streamApiRootUrl) {
    return url;
  }

  // 如果不是流式 API，返回原 URL
  if (!isStreamingAPI(url)) {
    return url;
  }

  // 移除 baseUrl 末尾的斜杠
  const baseUrl = streamApiRootUrl.replace(/\/$/, '');

  // 如果 URL 以 / 开头，直接拼接
  if (url.startsWith('/')) {
    return `${baseUrl}${url}`;
  }

  // 否则添加 /
  return `${baseUrl}/${url}`;
}

async function customFetch(url: string, options: any = {}) {
  try {
    const token = localStorage.getItem('apiAccessToken');
    const languagePreference = loadLanguagePreference() || 'en';
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept-Language': languagePreference,
      'x-platform': 'web',
    };
    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    // 重写流式 API 的 URL（如果配置了 STREAM_API_ROOT_URL）
    url = rewriteStreamingAPIUrl(url);

    // // 为流式接口添加标记头（辅助服务器识别，但不能强制协议版本）
    // if (isStreamingAPI(url)) {
    //   options.headers = {
    //     ...options.headers,
    //     'X-Stream-Request': 'true',
    //   };
    // }

    options.headers = {
      ...defaultHeaders,
      ...withRequestId(options.headers ?? {}),
    };
    if (isStreamingAPI(url)) {
      options.credentials = 'include';
    }
    const response = await fetch(url, options);
    // return fetch(url, options);
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('apiAccessToken');
        localStorage.removeItem('currentUserInfo');
        clearLocalSwrCache();
        return;
      } else if (response.status === 400) {
        return response;
      }
      throw new Error(`Request failed, status code: ${response.status}`);
    }

    return response;
  } catch (error: any) {
    // 捕获任何错误，并返回格式化的错误信息
    return error; //{ success: false, error: error?.message };
  }
}

export default customFetch;

export async function researchFetch(url: string, options: any = {}) {
  try {
    const token = localStorage.getItem('researchAccessToken');
    const languagePreference = loadLanguagePreference() || 'en';

    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept-Language': languagePreference,
      lang: languagePreference,
    };
    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    // 检查是否是流式 API 并且配置了 STREAM_API_ROOT_URL
    let fullUrl: string;
    if (isStreamingAPI(url) && process.env.NEXT_PUBLIC_STREAM_API_ROOT_URL) {
      // 如果是流式 API 且配置了专用 URL，使用重写后的 URL
      fullUrl = rewriteStreamingAPIUrl(url);
    } else {
      // 否则使用默认的 researchDomain
      fullUrl = `${getResearchDomain(url)}`;
    }

    // // 为流式接口添加标记头（辅助服务器识别，但不能强制协议版本）
    // if (isStreamingAPI(fullUrl) || isStreamingAPI(url)) {
    //   options.headers = {
    //     ...options.headers,
    //     'X-Stream-Request': 'true',
    //   };
    // }

    options.headers = {
      ...defaultHeaders,
      ...withRequestId(options.headers ?? {}),
    };
    const response = await fetch(fullUrl, options);
    // return fetch(url, options);
    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('researchAccessToken');
        return;
      }
    }

    return response;
  } catch (error: any) {
    // 捕获任何错误，并返回格式化的错误信息
    throw error; //{ success: false, error: error?.message };
  }
}
